import { useId } from 'react';
import { Box, Paper, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import type { WeeklySignups } from '@/api/generated';
import { ChartDataTable } from '@/components/ChartDataTable';
import { formatCivilOrDash, formatCount } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme';

export interface SignupsBarsProps {
  series: WeeklySignups[];
}

export function SignupsBars({ series }: SignupsBarsProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const bars = series.slice(-12);
  const max = bars.reduce((acc, week) => Math.max(acc, week.signups), 0);
  const dash = t('affiliates.unavailable');
  const dataId = useId();
  const shown = bars.map((week) => ({
    key: week.weekStart,
    count: week.signups,
    countLabel: formatCount(week.signups, locale),
    week: formatCivilOrDash(week.weekStart, locale, dash, { month: 'short', day: 'numeric' }),
  }));

  return (
    <Paper sx={{ p: 2.5, minWidth: 0 }}>
      <Typography variant="h3" component="h3">
        {t('affiliates.barsTitle')}
      </Typography>
      {bars.length === 0 ? (
        <Typography variant="body2" sx={{ color: tokens.text2, mt: 1 }}>
          {t('affiliates.noSignups')}
        </Typography>
      ) : (
        <>
          <Box
            role="img"
            aria-label={t('affiliates.barsSummary', { count: bars.length })}
            aria-describedby={dataId}
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 1,
              height: 80,
              mt: 1.5,
              borderBottom: '1px solid',
              borderColor: tokens.line,
            }}
          >
            {shown.map((bar) => {
              const pct = max > 0 ? (bar.count / max) * 100 : 0;
              return (
                <Tooltip
                  describeChild
                  key={bar.key}
                  title={t('affiliates.barTip', { week: bar.week, count: bar.countLabel })}
                >
                  <Box
                    sx={{
                      flex: 1,
                      height: `${pct}%`,
                      bgcolor: tokens.brand,
                      minHeight: pct > 0 ? 4 : 0,
                    }}
                  />
                </Tooltip>
              );
            })}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.75 }}>
            {shown.map((bar) => (
              <Typography
                key={`lbl-${bar.key}`}
                variant="caption"
                sx={{ flex: 1, textAlign: 'center', color: tokens.text2 }}
              >
                {bar.week}
              </Typography>
            ))}
          </Box>
          <ChartDataTable
            id={dataId}
            label={t('affiliates.barsTitle')}
            columns={[t('affiliates.barsWeek'), t('affiliates.barsSignups')]}
            rows={shown.map((bar) => ({ key: bar.key, label: bar.week, values: [bar.countLabel] }))}
          />
        </>
      )}
    </Paper>
  );
}
